import { logger } from '@/lib/logger';
import { Link, Stack, usePathname, useRouter } from 'expo-router';
import { useEffect } from 'react';
import { Platform, StyleSheet } from 'react-native';

import { ThemedText } from '@/components/ThemedText';
import { ThemedView } from '@/components/ThemedView';
import { AccessDenied } from '@/components/ui/AccessDenied';
import { URLS } from '@/lib/constants';

/**
 * A path that is exactly one UUID segment is the tell-tale of a pasted
 * blob: URL from the protected PDF viewer (blob:https://app.../<uuid>).
 * Those are session-bound and resolve to nothing outside the tab that
 * created them, so the sharer's recipient lands here — show them the
 * paywall instead of a generic 404. Every other bad URL keeps the 404.
 */
const BLOB_UUID_PATH_RE =
  /^\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export default function NotFoundScreen() {
  const pathname = usePathname();
  const router = useRouter();
  const isSharedBlobUrl = BLOB_UUID_PATH_RE.test(pathname);

  useEffect(() => {
    const url = Platform.OS === 'web' && typeof window !== 'undefined'
      ? window.location.href
      : pathname;

    logger.error('Route 404: page not found', {
      pathname,
      url,
      sharedBlobUrl: isSharedBlobUrl,
      referrer: Platform.OS === 'web' && typeof document !== 'undefined'
        ? document.referrer
        : undefined,
    });
  }, [pathname, isSharedBlobUrl]);

  if (isSharedBlobUrl) {
    return (
      <>
        <Stack.Screen options={{ title: 'Premium Content' }} />
        <ThemedView style={styles.container}>
          <AccessDenied
            title="Premium Content"
            message="Upgrade your membership to access this file and unlock all premium features."
            onUpgrade={() => {
              if (Platform.OS !== 'web') {
                router.push('/upgrade');
                return;
              }
              if (typeof window === 'undefined') return;
              window.location.href = URLS.JOIN;
            }}
          />
        </ThemedView>
      </>
    );
  }

  return (
    <>
      <Stack.Screen options={{ title: 'Oops!' }} />
      <ThemedView style={styles.container}>
        <ThemedText type="title">This screen does not exist.</ThemedText>
        <Link href="/" style={styles.link}>
          <ThemedText type="link">Go to home screen!</ThemedText>
        </Link>
      </ThemedView>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  link: {
    marginTop: 15,
    paddingVertical: 15,
  },
});
